import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { sprintApi, TYPE_LABEL_KEY, type MockContent, type TypeTally } from '@/api/ieltsMock';
import { PageHeader } from '@/components/ui/PageHeader';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Mascot } from '@/components/ui/Mascot';
import { Card, CardTitle } from '@/components/ui/Card';
import { SelectionToolbar } from '@/components/ielts/SelectionToolbar';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { humanizeError } from '@/lib/errors';
import { MockSpeaking, MascotLoading } from './StudentIELTSMockPage';
import { TaskCard } from '@/components/ielts/MockTaskCard';
import { AnswerReview, type ReviewItem } from '@/components/ielts/AnswerReview';
import { SourceSplit } from '@/components/ielts/SourceSplit';
import { enqueue } from '@/lib/outbox';
import { useConfirm } from '@/components/ui/ConfirmProvider';

type SprintResult = {
  status: string;
  band_overall?: number;
  per_type_accuracy?: Record<string, TypeTally>;
  // Разбор по каждому вопросу — сервер кладёт его в попытку при сдаче.
  review?: ReviewItem[];
};

export function StudentIELTSSprintPage() {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [content, setContent] = useState<MockContent | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<'loading' | 'run' | 'submitting' | 'done' | 'error'>('loading');
  const [result, setResult] = useState<SprintResult | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [err, setErr] = useState('');

  const readingRef = useRef<HTMLDivElement>(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      const v = await sprintApi.start();
      setAttemptId(v.attempt.id);
      setContent(v.content);
      setAnswers(((v.attempt as { answers?: Record<string, string> }).answers) ?? {});
      setPhase('run');
    } catch (e) {
      setErr(humanizeError(e));
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // debounced autosave
  useEffect(() => {
    if (phase !== 'run' || !attemptId) return;
    const t = setTimeout(() => {
      // Через очередь: ключ привязан к попытке, поэтому новое автосохранение
      // заменяет предыдущее и очередь не растёт от каждого нажатия клавиши.
      void enqueue('sprint-progress', `sprint:${attemptId}`, {
        attemptId,
        answers: answersRef.current,
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [answers, phase, attemptId]);

  const onAnswer = useCallback((qid: string, val: string) => {
    setAnswers((a) => ({ ...a, [qid]: val }));
  }, []);

  const submit = useCallback(async () => {
    if (!attemptId) return;
    setPhase('submitting');
    try {
      const r = (await sprintApi.submit(attemptId, answersRef.current)) as SprintResult;
      setResult(r);
      setPhase('done');
      qc.invalidateQueries({ queryKey: ['ielts', 'profile'] });
    } catch (e) {
      setErr(humanizeError(e));
      setPhase('error');
    }
  }, [attemptId, qc]);

  const answered = useMemo(() => Object.values(answers).filter((v) => v !== '').length, [answers]);

  /**
   * Сколько заданий в спринте всего — чтобы понять, всё ли отвечено.
   *
   * ⚠️ СЧИТАЕМ ПО ФАКТИЧЕСКОМУ СОСТАВУ. Спринт собирается под слабые типы
   * КОНКРЕТНОГО ученика и у разных людей разный; зашитое число обещало бы
   * работу, которой нет.
   */
  const totalTasks = useMemo(() => {
    const c = content;
    if (!c) return 0;
    let n = 0;
    for (const pack of [c.listening ?? [], c.reading ?? []]) {
      for (const src of pack) {
        for (const task of src.tasks ?? []) n += (task.questions ?? []).length;
      }
    }
    n += (c.writing ?? []).length + (c.speaking ?? []).length;
    return n;
  }, [content]);

  if (phase === 'loading') return <MascotLoading text={t('sprint.loading')} />;
  if (phase === 'submitting') return <MascotLoading text={t('sprint.submitting')} />;
  if (phase === 'error')
    return (
      <Card variant="subtle" className="space-y-3">
        <p className="text-error">{err}</p>
        <Button onClick={() => void load()}>{t('common.retry')}</Button>
      </Card>
    );

  if (phase === 'done' && result) {
    const pta = result.per_type_accuracy ?? {};
    return (
      <>
        <PageHeader title={t('sprint.doneTitle')} subtitle={t('sprint.doneSubtitle')} />
        <div className="space-y-6">
          <Card className="flex flex-col items-center gap-1 text-center">
            <Mascot
              pose={(result.band_overall ?? 0) >= 6 ? 'celebrate' : 'encourage'}
              alt="Bil"
              className={`${(result.band_overall ?? 0) >= 6 ? 'mascot-celebrate' : 'mascot'} h-36 w-auto object-contain`}
            />
            <div className="text-sm text-on-surface-variant">
              {t((result.band_overall ?? 0) >= 6 ? 'sprint.scoreStrong' : 'sprint.scoreKeep')}
            </div>
            <div className="text-5xl font-bold text-primary">{result.band_overall ?? '—'}</div>
          </Card>
          <Card variant="subtle" className="space-y-2">
            <CardTitle>{t('sprint.perType')}</CardTitle>
            <div className="space-y-1.5">
              {Object.entries(pta).map(([type, tt]) => (
                <div key={type} className="flex items-center justify-between gap-3 text-sm">
                  <span>{t(TYPE_LABEL_KEY[type] ?? type)}</span>
                  <span className="tabular-nums text-on-surface-variant">
                    {tt.correct}/{tt.total} · {Math.round(tt.acc * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </Card>
          {/* Разбор по требованию: балл ученик видит сразу, а разбирать
              ошибки захочет не всегда — и он длинный. */}
          {(result.review?.length ?? 0) > 0 && (
            <div className="flex justify-center">
              <Button variant={showReview ? 'ghost' : 'primary'} onClick={() => setShowReview((v) => !v)}>
                <span className="material-symbols-outlined">fact_check</span>
                {t(showReview ? 'sprint.reviewHide' : 'sprint.reviewShow')}
              </Button>
            </div>
          )}

          {showReview && <AnswerReview items={result.review ?? []} />}

          <div className="flex justify-center gap-3">
            <Button onClick={() => void load()}>{t('sprint.again')}</Button>
            <Link to="/app/ielts/diagnostic">
              <Button variant="ghost">{t('sprint.myLevel')}</Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (!content) return null;

  /**
   * ⚠️ ЧАСТИ МОГУТ ОТСУТСТВОВАТЬ, И ЭТО НОРМА, А НЕ СБОЙ. Тариф открывает только
   * те навыки, за которые заплачено: у «чтение и слушание» письма и речи нет
   * вовсе, и сервер честно присылает пустоту.
   *
   * Страница же читала `writing.map(...)` напрямую и падала целиком с
   * «null is not an object» — вместо спринта ученик видел белый экран с
   * английской ошибкой (заказчик, 07.09.2026). Ровно та же поломка была в моке
   * 06.09.2026; здесь её не поправили.
   */
  const listening = content.listening ?? [];
  const reading = content.reading ?? [];
  const writing = content.writing ?? [];
  const speaking = content.speaking ?? [];

  return (
    <>
      <div className="sticky top-0 z-20 -mx-4 mb-4 flex items-center justify-between border-b border-outline bg-surface/95 px-4 py-3 backdrop-blur">
        <span className="text-sm font-medium">{t('sprint.barTitle')}</span>
        <span className="text-xs text-on-surface-variant">{t('ieltsMock.answered', { n: answered })}</span>
      </div>
      <PageHeader title={t('sprint.title')} subtitle={t('sprint.subtitle')} />

      <div className="space-y-8" ref={readingRef}>
        <SelectionToolbar containerRef={readingRef} />
        {listening.map((b) => (
          <SourceSplit
            key={b.audio.id}
            // Запись прячуть за вкладку нельзя: пауза и перемотка нужны прямо
            // во время ответа. Поэтому плеер закрепляется, а не уезжает.
            kind="audio"
            source={
              <Card className="space-y-2">
                <CardTitle>🎧 {b.audio.title || t('ieltsMock.audio')}</CardTitle>
                <AudioPlayer src={b.audio.audio_url} preload="none" />
              </Card>
            }
            questions={
              <div className="space-y-4">
                {b.tasks.map((t) => (
                  <TaskCard key={t.id} task={t} answers={answers} onAnswer={onAnswer} />
                ))}
              </div>
            }
          />
        ))}

        {reading.map((b) => (
          <SourceSplit
            key={b.text.id}
            paneClassName="max-h-[70vh] overflow-y-auto lg:sticky lg:top-24 lg:rounded-[22px] lg:bg-surface-container lg:p-5"
            source={
              <Card className="lg:max-h-[70vh] lg:overflow-y-auto">
                <CardTitle>📖 {b.text.title}</CardTitle>
                <div className="mt-2 whitespace-pre-line text-sm leading-relaxed">{b.text.body}</div>
              </Card>
            }
            questions={
              <div className="space-y-4">
                {b.tasks.map((t) => (
                  <TaskCard key={t.id} task={t} answers={answers} onAnswer={onAnswer} />
                ))}
              </div>
            }
          />
        ))}

        {writing.map((w) => (
          <Card key={w.id} className="space-y-3">
            <Pill variant="primary">✍️ Writing</Pill>
            <p className="whitespace-pre-line text-sm">{w.prompt}</p>
            <textarea
              value={answers[w.id] ?? ''}
              onChange={(e) => onAnswer(w.id, e.target.value)}
              rows={8}
              placeholder={t('sprint.writePlaceholder')}
              className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
            />
          </Card>
        ))}

        {speaking.length > 0 && (
          <MockSpeaking
            prompts={speaking}
            done={!!answers['__speaking_done']}
            onDone={(transcript) => {
              if (speaking[0]) onAnswer(speaking[0].id, transcript);
              onAnswer('__speaking_done', '1');
            }}
          />
        )}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={async () => {
            /**
             * ⚠️ О НЕОТВЕЧЕННЫХ ГОВОРИМ ВСЛУХ. Они засчитываются как ошибки, и
             * человек об этом не догадывается: пустое поле выглядит как «ещё
             * вернусь», а на деле это минус балл. Заказчик 07.09.2026 просил
             * прямое сообщение «ты не завершил все задания».
             *
             * Сдать всё равно можно: упереться в кнопку, которая не нажимается,
             * хуже, чем предупреждение с цифрой.
             */
            const left = totalTasks - answered;
            const message =
              totalTasks > 0 && left > 0
                ? t('ieltsMock.finishIncomplete', { left })
                : t('sprint.finishConfirm');
            if (await ask({ message })) void submit();
          }}
        >
          {t('sprint.finishCta')}
        </Button>
      </div>
    </>
  );
}
